import { formatRelativeDate } from '@littlebird/ui-utils';
import { type ActionImpl } from 'kbar';
import { observer } from 'mobx-react-lite';
import {
  cloneElement,
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
  useMemo,
} from 'react';

import { Icons } from '@/assets/icons/icons';
import { cn } from '@/lib/utils';

import { splitMatch } from './highlight-match';
import { hasSubmenu } from './preview-model';

import type { CommandBarActionImpl } from './actions/types';

type Props = {
  action: ActionImpl;
  active: boolean;
  currentRootActionId: string | null | undefined;
  query: string;
};

export const CommandBarItem = observer(
  forwardRef<HTMLDivElement, Props>(
    (
      { action: baseAction, active, currentRootActionId, query }: Props,
      ref
    ) => {
      const action = baseAction as CommandBarActionImpl;
      const ancestors = useMemo(() => {
        if (!currentRootActionId) {
          return action.ancestors;
        }
        const index = action.ancestors.findIndex(
          (ancestor) => ancestor.id === currentRootActionId
        );
        return action.ancestors.slice(index + 1);
      }, [action.ancestors, currentRootActionId]);

      const isSeeAll = action.preview?.kind === 'see_all';
      const split = isSeeAll ? null : splitMatch(action.name, query);
      const meta =
        action.meta ??
        (!action.isSearchResult && typeof action.subtitle === 'string'
          ? action.subtitle
          : undefined);

      return (
        <Item active={active} ref={ref}>
          <Icon dangerous={action.dangerous}>
            {action.icon ? (
              // @ts-expect-error no icon on ActionImpl
              cloneElement(action.icon, {
                size: 14,
              })
            ) : (
              <Icons.chevronRight size={14} />
            )}
          </Icon>

          {ancestors.length > 0 && (
            <span className="min-w-0 shrink-[8] truncate text-foreground-muted">
              {ancestors.map((ancestor) => `${ancestor.name} ›`).join(' ')}
            </span>
          )}
          <span
            className={cn(
              'min-w-0 flex-auto truncate',
              action.dangerous && 'text-danger-11',
              isSeeAll && 'text-foreground-muted'
            )}
          >
            {split ? (
              <>
                {split.before}
                <mark className="bg-transparent font-semibold text-foreground">
                  {split.match}
                </mark>
                {split.after}
              </>
            ) : (
              action.name
            )}
          </span>
          {meta && ancestors.length === 0 && (
            <span className="max-w-[30%] flex-shrink truncate text-xs text-foreground-muted">
              {meta}
            </span>
          )}
          {action.date && (
            <span className="flex-shrink-0 text-xs text-foreground-muted">
              {formatRelativeDate(action.date)}
            </span>
          )}
          {action.selected && (
            <Icons.check
              size={14}
              className="flex-shrink-0 text-foreground-muted"
            />
          )}
          {hasSubmenu(action) && (
            <Icons.chevronRight
              size={14}
              className="flex-shrink-0 text-foreground-muted"
            />
          )}
        </Item>
      );
    }
  )
);

function Icon({
  children,
  dangerous,
}: {
  children: ReactNode;
  dangerous?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex w-5 items-center justify-center flex-shrink-0',
        dangerous ? 'text-danger-11' : 'text-foreground-muted'
      )}
    >
      {children}
    </div>
  );
}

const Item = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & { active?: boolean }
>(({ active, children, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'text-sm h-[34px] px-2 mx-2 rounded-md flex items-center gap-2.5 select-none min-w-0',
      active ? 'bg-surface-active' : '',
      className
    )}
    {...props}
  >
    {children}
  </div>
));
Item.displayName = 'Item';
